import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { theme } from '../../../../../theme';

interface PdfPreviewProps {
  file: File | null;
}

const PdfPreview: React.FC<PdfPreviewProps> = ({ file }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);

  const onDocumentLoadSuccess = (pdf: { numPages: number }) => {
    setNumPages(pdf.numPages);
    setCurrentPage(1);
    setLoading(false);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, numPages));
  };

  const handleLoadStart = () => {
    setLoading(true);
  };

  if (!file) return null;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Box
        sx={{
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: 2,
          padding: 2,
          background: '#fafafa',
          boxShadow: 2,
          minHeight: 500,
          minWidth: 720,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadStart={handleLoadStart}
          loading=""
        >
          <Page
            pageNumber={currentPage}
            width={700}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </Box>

      {numPages > 1 && (
        <Box display="flex" alignItems="center" gap={2} mt={1}>
          <Button
            variant="outlined"
            startIcon={<NavigateBeforeIcon />}
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          <Typography>
            Page {currentPage} of {numPages}
          </Typography>

          <Button
            variant="outlined"
            endIcon={<NavigateNextIcon />}
            onClick={handleNextPage}
            disabled={currentPage === numPages}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PdfPreview;